import React from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import RoomOutlinedIcon from '@mui/icons-material/RoomOutlined';
import ForestOutlinedIcon from '@mui/icons-material/ForestOutlined';
import ExecutiveDashboardMap from './ExecutiveDashboardMap';
import { useReportConfig } from '../../contexts/ReportConfigContext.jsx';
import ReportWidget from './ReportWidget';
import {
  getCarReservaLegalDashboardStatus,
  getPraDashboardStatus,
  getEmbargosDashboardStatus,
  getCadastralDashboardData,
  getCcirsDashboardStatus,
  getCndDashboardStatus,
  getIbamaCertidaoStatusFromDocs,
  getSobreposicoesDashboardStatus,
  getAreasRegistradasDashboardData,
  getLegalDashboardData,
} from '../../utils/reportCalculations';
import { resolveChipColorFromThresholds } from '../../utils/reportCalculations/dashboardChips';

const baseChipSx = {
  height: 20,
  fontSize: '0.75rem',
  borderRadius: '4px',
  fontWeight: 500,
  '.MuiChip-label': { padding: '0 8px' },
};

const getStatusChipSx = (statusColor) => {
  switch (statusColor) {
    case 'success':
      return {
        ...baseChipSx,
        backgroundColor: '#e6ffed',
        color: '#2b703b',
        borderColor: '#c3e6c9',
        borderWidth: 1,
        borderStyle: 'solid',
      };
    case 'warning':
      return {
        ...baseChipSx,
        backgroundColor: '#fff9e6',
        color: '#b98600',
        borderColor: '#f2e2ac',
        borderWidth: 1,
        borderStyle: 'solid',
      };
    case 'error':
      return {
        ...baseChipSx,
        backgroundColor: '#ffebe6',
        color: '#c53030',
        borderColor: '#f2bcae',
        borderWidth: 1,
        borderStyle: 'solid',
      };
    case 'default':
    default:
      return {
        ...baseChipSx,
        backgroundColor: '#f0f0f0',
        color: '#666666',
        borderColor: '#dddddd',
        borderWidth: 1,
        borderStyle: 'solid',
      };
  }
};

const formatLatLng = (lat, lng) => {
  if (typeof lat !== 'number' || typeof lng !== 'number') return 'N/D';
  const latStr = lat.toFixed(5);
  const lngStr = lng.toFixed(5);
  return `${latStr}, ${lngStr}`;
};

const formatPercentBr = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return null;
  return `${n.toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}%`;
};

const formatHa = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return 'N/D';
  return n.toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const formatDeltaSignedPercent = (value) => {
  if (value === null || value === undefined) return 'N/D';
  const n = Number(value);
  if (!Number.isFinite(n)) return 'N/D';

  const abs = Math.abs(n);
  const sign = n > 0 ? '+' : n < 0 ? '-' : '';

  const formatted = abs.toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  return `${sign}${formatted}%`;
};

const getDeltaStatusColor = (status) => {
  switch (status) {
    case 'error':
      return '#c53030';
    case 'warning':
      return '#b98600';
    case 'success':
      return '#2b703b';
    case 'default':
    default:
      return '#666666';
  }
};

export default function ReportPrintDashboard({
  snapshot,
  docsFiles,
  onReadyForPrint,
  printRefreshToken,
  mapSnapshots,
}) {
  const { config: reportConfig } = useReportConfig();
  const dashboardChipsConfig = reportConfig?.dashboard?.chips || {};

  const cadastral = getCadastralDashboardData(snapshot || {});
  const carReservaLegalStatus = getCarReservaLegalDashboardStatus(snapshot);
  const praStatus = getPraDashboardStatus(snapshot, dashboardChipsConfig);
  const embargosStatus = getEmbargosDashboardStatus(snapshot, dashboardChipsConfig);
  const ccirStatus = getCcirsDashboardStatus(snapshot || {}, dashboardChipsConfig);
  const cndStatus = getCndDashboardStatus(snapshot || {}, dashboardChipsConfig);
  const ibamaCertidaoStatus = getIbamaCertidaoStatusFromDocs(docsFiles || [], dashboardChipsConfig);
  const sobreposicoes = getSobreposicoesDashboardStatus(snapshot || {});
  const areasRegistradas = getAreasRegistradasDashboardData(snapshot || {}, dashboardChipsConfig);
  const legal = getLegalDashboardData(snapshot || {});

  const properties = snapshot?.properties || {};
  const sobreposicaoRaw = properties.sobreposicao;
  const sobreposicaoArray = Array.isArray(sobreposicaoRaw)
    ? sobreposicaoRaw
    : sobreposicaoRaw
      ? [sobreposicaoRaw]
      : [];

  const countTemaSobreposicao = (temaKey) => {
    if (!temaKey) return 0;
    let count = 0;

    sobreposicaoArray.forEach((item) => {
      const temas = item && item.temas ? item.temas : {};
      const raw = temas[temaKey];
      if (!raw) return;

      if (Array.isArray(raw)) {
        count += raw.length;
      } else {
        count += 1;
      }
    });

    return count;
  };

  const sobreposicoesTemaCounts = {
    uc: countTemaSobreposicao('uc'),
    florestas: countTemaSobreposicao('florestas'),
    aldeias: countTemaSobreposicao('aldeias'),
    quilombolas: countTemaSobreposicao('quilombolas'),
    assentamentos: countTemaSobreposicao('assentamentos'),
    terras_indigenas: countTemaSobreposicao('terras_indigenas'),
    mineracao: countTemaSobreposicao('mineracao'),
    embargos_ibama: countTemaSobreposicao('embargos_ibama'),
    pra: countTemaSobreposicao('pra'),
  };

  const sobreposicoesChipsRules = dashboardChipsConfig.sobreposicoes || {};

  const getSobreposicaoChipColor = (temaKey, fallbackColor) => {
    const rules = sobreposicoesChipsRules[temaKey];
    const count = sobreposicoesTemaCounts[temaKey] ?? 0;

    if (rules && Array.isArray(rules.thresholds)) {
      return resolveChipColorFromThresholds(rules, count, fallbackColor) || fallbackColor;
    }

    return fallbackColor;
  };

  const sobreposicoesChipColors = {
    uc: getSobreposicaoChipColor('uc', sobreposicoesTemaCounts.uc >= 1 ? 'warning' : 'default'),
    florestasPublicas: getSobreposicaoChipColor(
      'florestas',
      sobreposicoesTemaCounts.florestas >= 1 ? 'warning' : 'default',
    ),
    aldeiasIndigenas: getSobreposicaoChipColor(
      'aldeias',
      sobreposicoesTemaCounts.aldeias >= 1 ? 'warning' : 'default',
    ),
    quilombolas: getSobreposicaoChipColor(
      'quilombolas',
      sobreposicoesTemaCounts.quilombolas >= 1 ? 'warning' : 'default',
    ),
    assentamentosIncra: getSobreposicaoChipColor(
      'assentamentos',
      sobreposicoesTemaCounts.assentamentos >= 1 ? 'warning' : 'default',
    ),
    terrasIndigenas: getSobreposicaoChipColor(
      'terras_indigenas',
      sobreposicoesTemaCounts.terras_indigenas >= 1 ? 'warning' : 'default',
    ),
    mineracao: getSobreposicaoChipColor(
      'mineracao',
      sobreposicoesTemaCounts.mineracao >= 1 ? 'warning' : 'default',
    ),
    embargosIbama: getSobreposicaoChipColor(
      'embargos_ibama',
      sobreposicoesTemaCounts.embargos_ibama >= 1 ? 'error' : 'default',
    ),
    pra: getSobreposicaoChipColor('pra', sobreposicoesTemaCounts.pra >= 1 ? 'warning' : 'default'),
  };

  const dashboardMapUrl = mapSnapshots && mapSnapshots['dashboard-main'];

  const {
    nomePropriedade,
    uf,
    municipio,
    lat,
    lng,
    _pfCount,
    _pjCount,
    biomas,
    car,
    sigef,
    sncr,
    cafir,
  } = cadastral || {};

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Grid container spacing={1.5}>
        <Grid item xs={12} md={4}>
          <ReportWidget sectionKey="dashboard" widgetKey="dashboard_main_map">
            <Card variant="outlined" sx={{ height: { xs: 180, md: 200 } }}>
              <CardContent sx={{ p: 0.75 }}>
                <Box sx={{ height: { xs: 160, md: 180 } }}>
                  {dashboardMapUrl ? (
                    <Box
                      component="img"
                      src={dashboardMapUrl}
                      alt="Mapa da propriedade (dashboard)"
                      sx={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        borderRadius: 1,
                        display: 'block',
                      }}
                    />
                  ) : (
                    <ExecutiveDashboardMap
                      snapshot={snapshot}
                      forPrint
                      onMapReady={onReadyForPrint}
                      printRefreshToken={printRefreshToken}
                    />
                  )}
                </Box>
              </CardContent>
            </Card>
          </ReportWidget>
        </Grid>

        <Grid item xs={12} md={8}>
          <ReportWidget sectionKey="dashboard" widgetKey="dashboard_cadastral_summary">
            <Card variant="outlined">
              <CardContent
                sx={{
                  py: 0.75,
                  px: 1.25,
                  '&:last-child': { pb: 0.75 },
                }}
              >
                <Grid container spacing={1.5} sx={{ mb: 0.75 }}>
                  <Grid item xs={12} md={6}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontWeight: 600, display: 'block' }}
                    >
                      Nome da Propriedade
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {nomePropriedade || '-'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontWeight: 600, display: 'block' }}
                    >
                      Município / UF
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {municipio || uf ? `${municipio || 'N/D'}${uf ? ` / ${uf}` : ''}` : '-'}
                    </Typography>
                  </Grid>
                </Grid>

                <Grid container spacing={2} sx={{ mb: 1 }}>
                  <Grid item xs={12} md={4}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <RoomOutlinedIcon
                        fontSize="small"
                        sx={{ color: 'text.secondary', mt: '2px' }}
                      />
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontWeight: 600, display: 'block' }}
                      >
                        Lat, Lon
                      </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {formatLatLng(lat, lng)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={8}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <ForestOutlinedIcon
                        fontSize="small"
                        sx={{ color: 'text.secondary', mt: '2px' }}
                      />
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontWeight: 600, display: 'block' }}
                      >
                        Biomas
                      </Typography>
                    </Box>
                    <Box sx={{ mt: 0.5, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {Array.isArray(biomas) && biomas.length > 0 ? (
                        biomas.map((b, index) => {
                          const pctLabel =
                            b?.pct !== null && b?.pct !== undefined ? formatPercentBr(b.pct) : null;
                          const label = pctLabel ? `${b.bioma} - ${pctLabel}` : b.bioma;
                          return (
                            <Chip
                              key={`${b.bioma || 'bioma'}-${index}`}
                              size="small"
                              label={label}
                              sx={getStatusChipSx('default')}
                            />
                          );
                        })
                      ) : (
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          -
                        </Typography>
                      )}
                    </Box>
                  </Grid>
                </Grid>

                <Box sx={{ mt: 0.75 }}>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {car && (
                      <Chip
                        size="small"
                        label={`CAR (${car.count ?? 0})`}
                        sx={getStatusChipSx(car.color || 'default')}
                      />
                    )}
                    {sigef && (
                      <Chip
                        size="small"
                        label={`SIGEF (${sigef.count ?? 0})  Parcelas (${sigef.parcelas ?? 0})`}
                        sx={getStatusChipSx(sigef.color || 'default')}
                      />
                    )}
                    {sncr && (
                      <Chip
                        size="small"
                        label={`SNCR (${sncr.count ?? 0})`}
                        sx={getStatusChipSx(sncr.color || 'default')}
                      />
                    )}
                    {cafir && (
                      <Chip
                        size="small"
                        label={`CAFIR (${cafir.count ?? 0})`}
                        sx={getStatusChipSx(cafir.color || 'default')}
                      />
                    )}
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </ReportWidget>
        </Grid>
      </Grid>

      <Grid container spacing={1.5}>
        <Grid item xs={12} md={6}>
          <ReportWidget sectionKey="dashboard" widgetKey="dashboard_sobreposicoes">
            <Card variant="outlined">
              <CardContent
                sx={{
                  py: 0.75,
                  px: 1.25,
                  '&:last-child': { pb: 0.75 },
                }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  Sobreposições
                </Typography>
                <Grid container spacing={1}>
                  <Grid item xs={12} sm={6}>
                    <Chip
                      size="small"
                      label={sobreposicoes.uc.label}
                      sx={getStatusChipSx(
                        sobreposicoesChipColors.uc || sobreposicoes.uc.color || 'default',
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Chip
                      size="small"
                      label={sobreposicoes.florestasPublicas.label}
                      sx={getStatusChipSx(
                        sobreposicoesChipColors.florestasPublicas ||
                          sobreposicoes.florestasPublicas.color ||
                          'default',
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Chip
                      size="small"
                      label={sobreposicoes.aldeiasIndigenas.label}
                      sx={getStatusChipSx(
                        sobreposicoesChipColors.aldeiasIndigenas ||
                          sobreposicoes.aldeiasIndigenas.color ||
                          'default',
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Chip
                      size="small"
                      label={sobreposicoes.quilombolas.label}
                      sx={getStatusChipSx(
                        sobreposicoesChipColors.quilombolas ||
                          sobreposicoes.quilombolas.color ||
                          'default',
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Chip
                      size="small"
                      label={sobreposicoes.assentamentosIncra.label}
                      sx={getStatusChipSx(
                        sobreposicoesChipColors.assentamentosIncra ||
                          sobreposicoes.assentamentosIncra.color ||
                          'default',
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Chip
                      size="small"
                      label={sobreposicoes.terrasIndigenas.label}
                      sx={getStatusChipSx(
                        sobreposicoesChipColors.terrasIndigenas ||
                          sobreposicoes.terrasIndigenas.color ||
                          'default',
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Chip
                      size="small"
                      label={sobreposicoes.mineracao.label}
                      sx={getStatusChipSx(
                        sobreposicoesChipColors.mineracao ||
                          sobreposicoes.mineracao.color ||
                          'default',
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Chip
                      size="small"
                      label={sobreposicoes.embargosIbama.label}
                      sx={getStatusChipSx(
                        sobreposicoesChipColors.embargosIbama ||
                          sobreposicoes.embargosIbama.color ||
                          'default',
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Chip
                      size="small"
                      label={sobreposicoes.pra.label}
                      sx={getStatusChipSx(
                        sobreposicoesChipColors.pra || sobreposicoes.pra.color || 'default',
                      )}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </ReportWidget>
        </Grid>

        <Grid item xs={12} md={6}>
          <ReportWidget sectionKey="dashboard" widgetKey="dashboard_ambiental_certidoes">
            <Card variant="outlined">
              <CardContent
                sx={{
                  py: 1,
                  px: 1.5,
                  '&:last-child': { pb: 1 },
                }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  Ambiental
                </Typography>
                <Grid container spacing={1}>
                  <Grid item xs={12} sm={4}>
                    <Chip
                      size="small"
                      label={carReservaLegalStatus.label}
                      sx={getStatusChipSx(carReservaLegalStatus.color || 'default')}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Chip
                      size="small"
                      label={embargosStatus.label}
                      sx={getStatusChipSx(embargosStatus.color || 'default')}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Chip
                      size="small"
                      label={praStatus.label}
                      sx={getStatusChipSx(praStatus.color || 'default')}
                    />
                  </Grid>
                </Grid>

                <Box sx={{ mt: 1.5 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                    Certidões
                  </Typography>
                  <Grid container spacing={1}>
                    <Grid item xs={12} sm={6}>
                      <Chip
                        size="small"
                        label={ccirStatus?.label || 'CCIR'}
                        sx={getStatusChipSx(ccirStatus?.color || 'default')}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Chip
                        size="small"
                        label={cndStatus?.label || 'CND'}
                        sx={getStatusChipSx(cndStatus?.color || 'default')}
                      />
                    </Grid>
                    <Grid item xs={12} sm={12}>
                      <Chip
                        size="small"
                        label={ibamaCertidaoStatus?.label || 'IBAMA'}
                        sx={getStatusChipSx(ibamaCertidaoStatus?.color || 'default')}
                      />
                    </Grid>
                  </Grid>
                </Box>
              </CardContent>
            </Card>
          </ReportWidget>
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <ReportWidget sectionKey="dashboard" widgetKey="dashboard_areas_registradas">
            <Card variant="outlined">
              <CardContent
                sx={{
                  py: 1,
                  px: 1.5,
                  '&:last-child': { pb: 1 },
                }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  Áreas Registradas
                </Typography>
                <TableContainer
                  sx={{
                    width: 'auto',
                    display: 'inline-block',
                  }}
                >
                  <Table
                    size="small"
                    sx={{
                      width: 'auto',
                      tableLayout: 'auto',
                      '& th, & td': {
                        py: 0.25,
                        px: 0.75,
                        fontSize: '0.75rem',
                      },
                    }}
                  >
                    <TableHead>
                      <TableRow>
                        <TableCell>Fonte</TableCell>
                        <TableCell align="right">Área (ha)</TableCell>
                        <TableCell align="right">Δ% vs base</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow>
                        <TableCell>CAR</TableCell>
                        <TableCell align="right">
                          {formatHa(areasRegistradas?.car?.areaHa)}
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{
                            color: getDeltaStatusColor(areasRegistradas?.car?.status),
                          }}
                        >
                          {formatDeltaSignedPercent(areasRegistradas?.car?.deltaPercent)}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>SIGEF</TableCell>
                        <TableCell align="right">
                          {formatHa(areasRegistradas?.sigef?.areaHa)}
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{
                            color: getDeltaStatusColor(areasRegistradas?.sigef?.status),
                          }}
                        >
                          {formatDeltaSignedPercent(areasRegistradas?.sigef?.deltaPercent)}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>CAFIR</TableCell>
                        <TableCell align="right">
                          {formatHa(areasRegistradas?.cafir?.areaHa)}
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{
                            color: getDeltaStatusColor(areasRegistradas?.cafir?.status),
                          }}
                        >
                          {formatDeltaSignedPercent(areasRegistradas?.cafir?.deltaPercent)}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>
                          {areasRegistradas?.base?.fonte
                            ? `BASE (${areasRegistradas.base.fonte})`
                            : 'BASE'}
                        </TableCell>
                        <TableCell align="right">
                          {formatHa(areasRegistradas?.base?.areaHa)}
                        </TableCell>
                        <TableCell align="right">—</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </ReportWidget>
        </Grid>

        <Grid item xs={12} md={6}>
          <ReportWidget sectionKey="dashboard" widgetKey="dashboard_legal_summary">
            <Card variant="outlined">
              <CardContent
                sx={{
                  py: 1,
                  px: 1.5,
                  '&:last-child': { pb: 1 },
                }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  Legal
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Box
                      sx={{
                        bgcolor: 'grey.100',
                        borderRadius: 1,
                        p: 1,
                      }}
                    >
                      <Typography
                        variant="subtitle2"
                        align="center"
                        sx={{ mb: 1, fontWeight: 600, fontSize: '0.8rem' }}
                      >
                        Civil
                      </Typography>
                      <Grid container spacing={0.5} sx={{ mb: 1 }}>
                        <Grid item xs={12}>
                          <Typography variant="caption" color="text.secondary">
                            Processos: {legal?.civil?.totalCount ?? 0}
                          </Typography>
                        </Grid>
                        <Grid item xs={12}>
                          <Typography variant="caption" color="text.secondary">
                            Valor em litígio: R$
                            {formatHa(legal?.civil?.valorLitigio)?.replace('N/D', ' 0,00')}
                          </Typography>
                        </Grid>
                      </Grid>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        <Chip
                          size="small"
                          label={`Ativo: ${legal?.civil?.ativos ?? 0}`}
                          sx={{
                            ...baseChipSx,
                            backgroundColor: '#e6ffed',
                            color: '#2b703b',
                            borderColor: '#c3e6c9',
                            borderWidth: 1,
                            borderStyle: 'solid',
                          }}
                        />
                        <Chip
                          size="small"
                          label={`Arquivado: ${legal?.civil?.arquivados ?? 0}`}
                          sx={{
                            ...baseChipSx,
                            backgroundColor: '#f0f0f0',
                            color: '#666666',
                            borderColor: '#dddddd',
                            borderWidth: 1,
                            borderStyle: 'solid',
                          }}
                        />
                        <Chip
                          size="small"
                          label={`Inativo: ${legal?.civil?.inativos ?? 0}`}
                          sx={{
                            ...baseChipSx,
                            backgroundColor: '#fff9e6',
                            color: '#b98600',
                            borderColor: '#f2e2ac',
                            borderWidth: 1,
                            borderStyle: 'solid',
                          }}
                        />
                      </Box>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Box
                      sx={{
                        bgcolor: 'grey.100',
                        borderRadius: 1,
                        p: 1,
                      }}
                    >
                      <Typography
                        variant="subtitle2"
                        align="center"
                        sx={{ mb: 1, fontWeight: 600, fontSize: '0.8rem' }}
                      >
                        Criminal
                      </Typography>
                      <Grid container spacing={0.5} sx={{ mb: 1 }}>
                        <Grid item xs={12}>
                          <Typography variant="caption" color="text.secondary">
                            Processos: {legal?.criminal?.totalCount ?? 0}
                          </Typography>
                        </Grid>
                        <Grid item xs={12}>
                          <Typography variant="caption" color="text.secondary">
                            Valor em litígio: R$
                            {formatHa(legal?.criminal?.valorLitigio)?.replace('N/D', ' 0,00')}
                          </Typography>
                        </Grid>
                      </Grid>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        <Chip
                          size="small"
                          label={`Ativo: ${legal?.criminal?.ativos ?? 0}`}
                          sx={{
                            ...baseChipSx,
                            backgroundColor: '#e6ffed',
                            color: '#2b703b',
                            borderColor: '#c3e6c9',
                            borderWidth: 1,
                            borderStyle: 'solid',
                          }}
                        />
                        <Chip
                          size="small"
                          label={`Arquivado: ${legal?.criminal?.arquivados ?? 0}`}
                          sx={{
                            ...baseChipSx,
                            backgroundColor: '#f0f0f0',
                            color: '#666666',
                            borderColor: '#dddddd',
                            borderWidth: 1,
                            borderStyle: 'solid',
                          }}
                        />
                        <Chip
                          size="small"
                          label={`Inativo: ${legal?.criminal?.inativos ?? 0}`}
                          sx={{
                            ...baseChipSx,
                            backgroundColor: '#fff9e6',
                            color: '#b98600',
                            borderColor: '#f2e2ac',
                            borderWidth: 1,
                            borderStyle: 'solid',
                          }}
                        />
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </ReportWidget>
        </Grid>
      </Grid>
    </Box>
  );
}
